'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaChevronRight, FaLocationArrow } from 'react-icons/fa';
import { FiCheck, FiCpu, FiLayers, FiMail, FiPhone, FiTrendingUp, FiUsers } from 'react-icons/fi';
import { PhoneInput } from 'react-international-phone';
import { Button } from '../ui';
const contacts = [
  {
    icon: <FiPhone />,
    text: '+91-771-730-0314',
    href: 'tel:+7717300314',
  },
  {
    icon: <FiMail />,
    text: 'info@makemybrandlabs.com',
    href: 'mailto:info@makemybrandlabs.com',
  },
  {
    icon: <FaLocationArrow />,
    text: 'C-136, Industrial Area, Phase 8, Chandigarh, 160071',
  },
];

const serviceOptions = [
  {
    category: "Design/Development-as-a-Service",
    icon: <FiLayers className="text-gray-700" />,
    services: ["UI/UX Design", "Graphic Design", "Website Development"]
  },
  {
    category: "Growth-as-a-Service",
    icon: <FiTrendingUp className="text-gray-700" />,
    services: ["Organic Visibility", "Performance Marketing", "Content Marketing"]
  },
  {
    category: "AI-as-a-Service",
    icon: <FiCpu className="text-gray-700" />,
    services: ["AI Consulting", "Machine Learning", "Automation Solutions"]
  },
  {
    category: "Staff-as-a-Service",
    icon: <FiUsers className="text-gray-700" />,
    services: ["Dedicated Developers", "Project Managers", "Technical Consultants"]
  }
];
const MainContactUs = () => {
  const router = useRouter();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    website: '',
    services: '',
    nda: false
  });

  const [files, setFiles] = useState<File[]>([]);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Custom animated checkbox markup
  const renderAnimatedCheckbox = (checked: boolean, onChange: () => void) => (
    <div
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      role="checkbox"
      aria-checked={checked}
      className={`relative w-5 h-5 border-2 rounded cursor-pointer transition-all duration-200 ${checked
        ? 'bg-blue-600 border-blue-600'
        : 'bg-white border-gray-300 hover:border-blue-400'
        }`}
    >
      {checked && (
        <FiCheck
          className="absolute inset-0 text-white w-3 h-3 m-0.5 transition-all duration-200 animate-in fade-in-0 zoom-in-50"
        />
      )}
    </div>
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    let fieldValue: string | boolean = value;
    if (type === 'checkbox' && 'checked' in e.target) {
      fieldValue = e.target.checked;
    }

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    if (submitError) setSubmitError(null);

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // inline validation per field (only if touched)
    const strVal = String(fieldValue ?? '').toString();
    const err = validateField(name, strVal, false);
    setFieldErrors(prev => ({ ...prev, [name]: err || '' }));
  };

  const handlePhoneChange = (phone: string) => {
    setFormData(prev => ({
      ...prev,
      phone: phone
    }));

    if (submitError) setSubmitError(null);

    // Mark phone field as touched
    setTouchedFields(prev => ({ ...prev, phone: true }));

    // inline validation for phone (only if touched)
    const err = validateField('phone', phone, false);
    setFieldErrors(prev => ({ ...prev, phone: err || '' }));
  };

  const validateField = (name: string, value: string, forceValidation = false): string | undefined => {
    // Only validate if field is touched or validation is forced (for form submission)
    if (!forceValidation && !touchedFields[name]) {
      return undefined;
    }

    switch (name) {
      case 'firstName':
      case 'lastName':
        return value.trim().length < 2 ? 'This field must be at least 2 characters' : undefined;
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : undefined;
      }
      case 'phone': {
        if (!value) return undefined;
        // Basic validation for international phone numbers
        // Allow + at start, then country code and number
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        const cleaned = value.replace(/[\s\-\(\)]/g, '');
        return !phoneRegex.test(cleaned) ? 'Please enter a valid international phone number' : undefined;
      }
      case 'website': {
        if (!value) return undefined;

        // Check if it's just numbers or random characters
        const isOnlyNumbers = /^\d+$/.test(value);
        const isRandomString = /^[a-zA-Z0-9]+$/.test(value) && value.length < 3;

        if (isOnlyNumbers) {
          return 'Please enter a valid website URL, not just numbers';
        }

        if (isRandomString) {
          return 'Please enter a complete website URL';
        }

        // Check for basic URL structure
        const hasProtocol = /^https?:\/\//.test(value);
        const hasDomain = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}/.test(value);
        const hasValidPath = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}(\/.*)?$/.test(value);

        if (!hasProtocol && !hasDomain && !hasValidPath) {
          return 'Please enter a valid website URL (e.g., example.com or https://example.com)';
        }

        try {
          // allow missing protocol by attempting to prepend https
          const toTest = /^(https?:)?\/\//.test(value) ? value : `https://${value}`;
          const url = new URL(toTest);

          // Additional validation for proper domain
          if (!url.hostname.includes('.')) {
            return 'Please enter a valid domain name with extension (e.g., .com, .org)';
          }

          // Check for valid TLD
          const tldPattern = /\.[a-zA-Z]{2,}$/;
          if (!tldPattern.test(url.hostname)) {
            return 'Please enter a valid domain with proper extension';
          }

          return undefined;
        } catch {
          return 'Please enter a valid website URL';
        }
      }
      case 'message':
        return value.trim().length < 10 ? 'Message must be at least 10 characters' : undefined;
      default:
        return undefined;
    }
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleFiles = (selectedFiles: FileList) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(selectedFiles).forEach(file => {
      const fileSizeInMB = file.size / (1024 * 1024);

      if (fileSizeInMB > 2) {
        errors.push(`${file.name}: File size must be less than 2MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setSubmitError(errors.join(', '));
    } else {
      setSubmitError(null);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleServiceSelect = (service: string) => {
    if (service === "Other") {
      const next = ["Other"];
      setSelectedServices(next);
      setFormData(prev => ({ ...prev, services: next.join(", ") }));
    } else {
      setSelectedServices(prev => {
        const withoutOther = prev.filter(s => s !== "Other");
        const next = withoutOther.includes(service)
          ? withoutOther.filter(s => s !== service)
          : [...withoutOther, service];
        setFormData(f => ({ ...f, services: next.join(", ") }));
        return next;
      });
    }
  };

  const handleCategoryToggle = (_category: string, servicesInCategory: string[]) => {
    setSelectedServices(prev => {
      const withoutOther = prev.filter(s => s !== "Other");
      const allSelected = servicesInCategory.every(s => withoutOther.includes(s));
      let next: string[];
      if (allSelected) {
        // unselect all in this category
        next = withoutOther.filter(s => !servicesInCategory.includes(s));
      } else {
        // select all in this category (merge unique)
        const set = new Set<string>([...withoutOther, ...servicesInCategory]);
        next = Array.from(set);
      }
      setFormData(f => ({ ...f, services: next.join(", ") }));
      return next;
    });
  };

  const handleDropdownClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Only require reCAPTCHA verification if it's configured
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && !captchaVerified) {
      setSubmitError('Please verify that you are not a robot.');
      return;
    }

    // Basic + per-field validation (force validation for form submission)
    const errors: { [k: string]: string } = {};
    (['firstName', 'lastName', 'email', 'phone', 'website', 'message'] as const).forEach((key) => {
      const err = validateField(key, String(formData[key as keyof typeof formData] ?? ''), true);
      if (err) errors[key] = err;
    });
    if (!formData.firstName.trim()) errors.firstName = errors.firstName || 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = errors.lastName || 'Last name is required';
    if (!formData.email.trim()) errors.email = errors.email || 'Email is required';
    if (!formData.message.trim()) errors.message = errors.message || 'Message is required';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitError('Please fix the highlighted fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData object for file uploads
      const submitData = new FormData();

      // Add form fields
      submitData.append('firstName', formData.firstName.trim());
      submitData.append('lastName', formData.lastName.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('phone', formData.phone.trim());
      submitData.append('message', formData.message.trim());
      submitData.append('website', formData.website.trim());
      submitData.append('services', formData.services.trim());
      submitData.append('nda', formData.nda.toString());

      // Add files
      files.forEach((file) => {
        submitData.append('files', file);
      });

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (response.ok) {
        // Reset form before redirecting to thank you page
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: '',
          website: '',
          services: '',
          nda: false
        });
        setFiles([]);
        setCaptchaVerified(false);
        setSubmitError(null);
        setFieldErrors({});
        setTouchedFields({});
        setSelectedServices([]);
        setExpandedCategories(new Set());
        setIsDropdownOpen(false);

        // Redirect to thank you page on success
        router.push('/thankyou');
        return;
      } else {
        // Handle specific error types
        if (response.status === 413) {
          setSubmitError('File size too large. Please ensure all files are less than 2MB.');
        } else if (response.status === 400 && result.message?.includes('file size')) {
          setSubmitError(result.message || 'File size validation failed. Please check your files.');
        } else {
          setSubmitError(result.error || result.message || 'Failed to submit form. Please try again.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('An error occurred while submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const stats = [
    {
      title: '2 Billion Plus Revenue',
      description: 'Generated for Our Clients Globally'
    },
    {
      title: 'Over 400 Global Clients',
      description: 'Who can vouch for our service'
    },
    {
      title: '4.8/5.0 Average Rating',
      description: 'From over 150 reviews from our clients'
    },
    {
      title: '5.8 Million Leads Captured',
      description: 'For projects in a wide range of industries.'
    }
  ];
  return (
    <section className="overflow-hidden">
      <div className=" max-w-7xl  w-full   z-20 bg-white shadow-[0_4px_54px_0_rgba(0,0,0,0.05)]   p-6 sm:p-12 lg:p-16   left-1/2 transform  mx-auto">
        {/* Heading */}
        <div className="mb-6 sm:mb-8 lg:mb-10">
          <p className="text-gray-600 text-base sm:text-lg">Get Started</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 leading-tight">
            Get in touch with us. We&apos;re here to assist you.
          </h1>
        </div>


        {submitError && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <p className="font-medium">Error:</p>
            <p>{submitError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Inputs */}
          <div>
            <label htmlFor="firstName" className="sr-only">First Name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${fieldErrors.firstName ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'}`}
              placeholder="First Name"
              required
            />
            {fieldErrors.firstName && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.firstName}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="sr-only">Last Name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${fieldErrors.lastName ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'}`}
              placeholder="Last Name"
              required
            />
            {fieldErrors.lastName && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.lastName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="sr-only">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${fieldErrors.email ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'}`}
              placeholder="Email"
              required
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="sr-only">Phone</label>
            <PhoneInput
              defaultCountry="in"
              value={formData.phone}
              onChange={handlePhoneChange}
              inputStyle={{
                width: '100%',
                border: 'none',
                borderBottom: fieldErrors.phone ? '1px solid #fca5a5' : '1px solid #d1d5db',
                padding: '12px 0',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'transparent',
                transition: 'border-color 0.2s',
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: 'none',
                  borderBottom: fieldErrors.phone ? '1px solid #fca5a5' : '1px solid #d1d5db',
                  padding: '12px 0',
                  backgroundColor: 'transparent',
                  transition: 'border-color 0.2s',
                }
              }}
              placeholder="Phone"
            />
            {fieldErrors.phone && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="website" className="sr-only">Website Link</label>
            <input
              id="website"
              name="website"
              type="text"
              value={formData.website}
              onChange={handleInputChange}
              className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${fieldErrors.website ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'}`}
              placeholder="Website Link"
            />
            {fieldErrors.website && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.website}</p>
            )}
          </div>

          {/* Services Custom Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <label htmlFor="services" className="sr-only">Services</label>
            <div
              onClick={handleDropdownClick}
              className="w-full border-b border-gray-300 focus:outline-none focus:border-black py-3 text-sm sm:text-base cursor-pointer transition-all duration-200 flex items-center justify-between hover:border-gray-400"
            >
              <div className="min-h-[24px] flex-1 flex items-center gap-2 flex-wrap">
                {selectedServices.length === 0 ? (
                  <span className="text-gray-500">Select Services</span>
                ) : (
                  selectedServices.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 text-xs sm:text-sm border border-gray-200"
                    >
                      {s}
                    </span>
                  ))
                )}
              </div>
              <span className={`ml-2 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-90' : ''
                }`}>
                <FaChevronRight />
              </span>
            </div>

            {isDropdownOpen && (
              <div className="absolute custom-scrollbar top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto animate-in fade-in-0 slide-in-from-top-2 duration-200">
                {serviceOptions.map((category, catIdx) => (
                  <div key={catIdx}>
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                      <div
                        onClick={() => toggleCategory(category.category)}
                        className="flex items-center gap-3 cursor-pointer select-none hover:bg-gray-50/60 rounded px-2 py-1 transition-colors"
                      >
                        <span className="text-lg transition-transform duration-200">{category.icon}</span>
                        <span className="font-medium text-gray-900">{category.category}</span>
                        <span className={`ml-2 text-gray-400 transition-transform duration-200 ${expandedCategories.has(category.category) ? 'rotate-90' : ''
                          }`}>
                          <FaChevronRight />
                        </span>
                      </div>
                      <div className="shrink-0">
                        {renderAnimatedCheckbox(
                          category.services.every(s => selectedServices.includes(s)),
                          () => handleCategoryToggle(category.category, category.services)
                        )}
                      </div>
                    </div>

                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedCategories.has(category.category) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                      <div className="bg-gray-50">
                        {category.services.map((service, srvIdx) => (
                          <div
                            key={srvIdx}
                            onClick={() => handleServiceSelect(service)}
                            className="px-8 py-3 cursor-pointer hover:bg-gray-100 flex items-center gap-3 transition-colors duration-150"
                          >
                            {renderAnimatedCheckbox(
                              selectedServices.includes(service),
                              () => handleServiceSelect(service)
                            )}
                            <span className="text-sm text-gray-700 font-medium">{service}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="border-t border-gray-200">
                  <div
                    onClick={() => handleServiceSelect("Other")}
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 transition-colors duration-150"
                  >
                    {renderAnimatedCheckbox(
                      selectedServices.includes("Other"),
                      () => handleServiceSelect("Other")
                    )}
                    <span className="text-sm font-medium text-gray-700">Other</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="message" className="sr-only">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
              className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 resize-vertical focus:outline-none ${fieldErrors.message ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-black'}`}
              placeholder="Message"
              required
            />
            {fieldErrors.message && (
              <p className="mt-1 text-xs text-red-500 font-medium">{fieldErrors.message}</p>
            )}
          </div>

          {/* Drag and drop file upload */}
          <div
            className="md:col-span-2 border-2 border-dashed border-gray-300 p-4 flex flex-col justify-center items-center text-gray-500 text-sm sm:text-base cursor-pointer hover:bg-gray-50 transition"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleClick}
          >
            <p className="text-center mb-0">Drag and drop or browse to upload your file(s)</p>
            <p className="text-xs text-gray-400 mt-1 mb-0">File size must be less than 2MB</p>
            {files.length > 0 && (
              <div className="mt-4 w-full max-w-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                <ul className="space-y-2">
                  {files.map((file, idx) => (
                    <li key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 truncate mb-0">{file.name}</p>
                        <p className="text-gray-500 text-xs mb-0">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(idx);
                        }}
                        className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0"
                        title="Remove file"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
              className="hidden"
            />
          </div>

          {/* NDA toggle + reCAPTCHA */}
          <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="nda"
                checked={formData.nda}
                onChange={handleInputChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
              <div
                className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 
    ${formData.nda ? 'translate-x-5' : 'translate-x-0'}`}
              ></div>
              <span className="ml-3 text-base font-medium text-[#1b1b1b]">I&apos;d like to sign an NDA</span>
            </label>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaVerified(!!token)}
                  ref={recaptchaRef}
                />
              ) : (
                <div className="text-sm text-gray-500 italic">
                  reCAPTCHA not configured - form will work without verification
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-start mt-6 sm:mt-8 md:col-span-2">
            <Button
              type="submit"
              size="lg"
              variant="primary"
              className="w-full sm:w-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-100" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </div>
              ) : (
                'Contact Us Today'
              )}
            </Button>
          </div>
        </form>

        <div className="mt-12 3xl:absolute 3xl:px-12 inset-x-0 mx-auto flex flex-col items-center">
          <div className=" mb-10  bg-[#ebeeb]/10 backdrop-blur-sm border border-[#1D214B]/15 p-8 mx-auto max-w-6xl">
            <div className="px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center lg:text-left">
              {stats.map((stat, idx) => (
                <div key={idx} className="flex flex-col items-center lg:items-start">
                  <h3 className="text-lg sm:text-xl  font-bold text-[#1b1b1b]">{stat.title}</h3>
                  <p className="text-[#1b1b1b] mt-2 text-lg sm:text-base">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4 ">
            <h3 className="text-red-600 font-semibold mr-4">Our contacts</h3>
            <div className="flex flex-col md:flex-row gap-4">
              {contacts.map((contact, index) => (
                <a
                  key={index}
                  href={contact.href}
                  className="flex items-center gap-2 bg-white px-5 py-2 rounded-full shadow hover:shadow-md transition"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-lg">{contact.icon}</span>
                  <span className="font-medium">{contact.text}</span>
                </a>
              ))}
            </div>
          </div>

        </div>


      </div>



    </section>
  );
};

export default MainContactUs;
